import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import vewlyLogoHorizontal from "@/assets/Logo_Horizonal_Transparent.png";
import vewlyLogoTransparent from "@/assets/Logo_transparent2.png";

interface WhiteLabelConfig {
  id: string;
  instance_name: string;
  custom_domain: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  brand_colors: any;
  email_config: any;
  features_enabled: any;
  is_active: boolean;
  // Computed properties with fallbacks
  effectiveLogoUrl: string;
  effectiveHeroLogoUrl: string;
}

export function useWhiteLabel() {
  return useQuery({
    queryKey: ['white-label-config'],
    queryFn: async () => {
      // First, check if we're on a custom domain
      const currentDomain = window.location.hostname;
      
      // Try to fetch config by domain
      const { data, error } = await supabase
        .from('white_label_configs')
        .select('*')
        .eq('custom_domain', currentDomain)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching white label config:', error);
        return null;
      }

      if (!data) return null;

      // Add computed properties with fallbacks to default Vewly logos
      return {
        ...data,
        effectiveLogoUrl: data.logo_url || vewlyLogoHorizontal,
        effectiveHeroLogoUrl: data.logo_url || vewlyLogoTransparent,
      } as WhiteLabelConfig;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export function useUserWhiteLabel() {
  return useQuery({
    queryKey: ['user-white-label-config'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('white_label_configs')
        .select('*')
        .eq('owner_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user white label config:', error);
        return null;
      }

      if (!data) return null;

      // Add computed properties with fallbacks to default Vewly logos
      return {
        ...data,
        effectiveLogoUrl: data.logo_url || vewlyLogoHorizontal,
        effectiveHeroLogoUrl: data.logo_url || vewlyLogoTransparent,
      } as WhiteLabelConfig;
    },
  });
}
